import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  Chip,
  Alert,
  CircularProgress,
  IconButton
} from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import {
  Mail as MailIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Refresh as RefreshIcon,
  Business as BusinessIcon,
  Group as GroupIcon
} from '@mui/icons-material';
import { useOrganization, Invitation } from '../../context/OrganizationContext';
import { formatDistanceToNow } from 'date-fns';
import { formatDate } from '../../utils/date-formatter';

// Helper function to safely format date
const formatExpirationDate = (expiresAt: string | undefined): string => {
  if (!expiresAt) return 'No expiration';
  
  const date = new Date(expiresAt);
  if (isNaN(date.getTime())) {
    return 'Invalid date';
  }
  
  try {
    return formatDistanceToNow(date, { addSuffix: true });
  } catch (error) {
    return 'Error formatting date';
  }
};

interface PersonalInvitationsProps {
  statusFilter?: 'all' | 'pending' | 'accepted' | 'declined' | 'expired' | 'canceled' | 'cancelled';
}

const PersonalInvitations: React.FC<PersonalInvitationsProps> = ({ statusFilter = 'pending' }) => {
  const [actionInProgress, setActionInProgress] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionSuccess, setActionSuccess] = useState<string | null>(null);

  const {
    invitations,
    acceptInvitation,
    declineInvitation,
    refreshInvitations,
    isLoading,
    error
  } = useOrganization();

  useEffect(() => {
    refreshInvitations();
  }, [refreshInvitations]);

  const getStatusText = (status: string | number) => {
    const statusStr = String(status);
    switch (statusStr) {
      case '0':
      case 'pending': return 'pending';
      case '1':
      case 'accepted': return 'accepted';
      case '2':
      case 'declined': return 'declined';
      case '3':
      case 'expired': return 'expired';
      case '4':
      case 'canceled':
      case 'cancelled': return 'canceled';
      default: return statusStr;
    }
  };

  const getStatusColor = (status: string | number) => {
    const statusStr = String(status);
    switch (statusStr) {
      case 'pending':
      case '0': return 'warning';
      case 'accepted':
      case '1': return 'success';
      case 'declined':
      case '2': return 'error';
      case 'expired':
      case '3': return 'default';
      case 'canceled':
      case 'cancelled':
      case '4': return 'default';
      default: return 'default';
    }
  };

  const columns: GridColDef<Invitation>[] = [
    {
      field: 'organizationName',
      headerName: 'Organization',
      flex: 1,
      minWidth: 200,
      renderCell: (params: GridRenderCellParams<Invitation, string>) => (
        <Box display="flex" alignItems="center" gap={1}>
          <BusinessIcon color="primary" fontSize="small" />
          {params.value || 'Unknown Organization'}
        </Box>
      ),
    },
    {
      field: 'scope',
      headerName: 'Type',
      width: 180,
      renderCell: (params: GridRenderCellParams<Invitation, string>) => (
        <Box display="flex" alignItems="center" gap={1}>
          {params.value === 'organization' ?
            <BusinessIcon color="primary" fontSize="small" /> :
            <GroupIcon color="secondary" fontSize="small" />
          }
          <Chip
            label={params.value}
            color={params.value === 'organization' ? 'primary' : 'secondary'}
            size="small"
          />
        </Box>
      ),
    },
    {
      field: 'teamName',
      headerName: 'Team',
      flex: 1,
      minWidth: 150,
      renderCell: (params: GridRenderCellParams<Invitation, string>) => (
        params.value || '-'
      ),
    },
    {
      field: 'roleName',
      headerName: 'Role',
      width: 120,
      renderCell: (params: GridRenderCellParams<Invitation, string>) => (
        params.value || 'Not specified'
      ),
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 100,
      renderCell: (params: GridRenderCellParams<Invitation, string | number>) => {
        const statusText = getStatusText(params.value ?? '');
        return (
          <Chip
            label={statusText}
            color={getStatusColor(params.value ?? '') as any}
            size="small"
          />
        );
      },
    },
    {
      field: 'createdAt',
      headerName: 'Received',
      width: 120,
      renderCell: (params: GridRenderCellParams<Invitation, string>) => {
        const date = params.value;
        return date ? formatDate(date) : '-';
      },
    },
    {
      field: 'expiresAt',
      headerName: 'Expires',
      width: 120,
      renderCell: (params: GridRenderCellParams<Invitation, string>) => {
        const invitation = params.row;
        // Only show expiration for pending invitations - handle both string and numeric formats
        const statusStr = String(invitation.status);
        const isPending = invitation.status === 'pending' || statusStr === '0' || invitation.status === 0;
        if (!isPending) {
          return '-';
        }

        const date = params.value;
        if (!date) return 'No expiration';
        try {
          return formatDistanceToNow(new Date(date), { addSuffix: true });
        } catch (error) {
          return 'Invalid date';
        }
      },
    },
    {
      field: 'actions',
      headerName: 'Actions',
      type: 'actions',
      width: 140,
      renderCell: (params: GridRenderCellParams<Invitation>) => {
        const invitation = params.row;
        // Check for pending status - handle both string and numeric formats
        const statusStr = String(invitation.status);
        const isPending = invitation.status === 'pending' || statusStr === '0' || invitation.status === 0;
        if (!isPending) return null;
        
        return (
          <Box display="flex" gap={1}>
            <IconButton
              size="small"
              color="primary"
              onClick={() => handleAcceptInvitation(invitation.id)}
              disabled={!!actionInProgress}
            >
              {actionInProgress === invitation.id ? <CircularProgress size={16} /> : <CheckIcon />}
            </IconButton>
            <IconButton
              size="small"
              color="error"
              onClick={() => handleDeclineInvitation(invitation.id)}
              disabled={!!actionInProgress}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        );
      },
    },
  ];

  const handleAcceptInvitation = async (invitationId: string) => {
    setActionInProgress(invitationId);
    setActionError(null);
    setActionSuccess(null);
    try {
      await acceptInvitation(invitationId);
      setActionSuccess('Invitation accepted successfully! You can now access the team.');
    } catch (error: any) {
      console.error('Error accepting invitation:', error);
      // Extract meaningful error message from the error object
      const errorMessage = error?.response?.data?.error?.message ||
                          error?.message ||
                          'Failed to accept invitation. Please try again.';
      setActionError(errorMessage);
    } finally {
      setActionInProgress(null);
    }
  };

  const handleDeclineInvitation = async (invitationId: string) => {
    setActionInProgress(invitationId);
    setActionError(null);
    setActionSuccess(null);
    try {
      await declineInvitation(invitationId);
      setActionSuccess('Invitation declined successfully.');
    } catch (error: any) {
      console.error('Error declining invitation:', error);
      // Extract meaningful error message from the error object
      const errorMessage = error?.response?.data?.error?.message ||
                          error?.message ||
                          'Failed to decline invitation. Please try again.';
      setActionError(errorMessage);
    } finally {
      setActionInProgress(null);
    }
  };


  const pendingCount = invitations?.filter(inv => inv?.status === 'pending').length || 0;

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Box>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <MailIcon />
            Personal Invitations
            {pendingCount > 0 && (
              <Chip 
                label={pendingCount} 
                color="warning" 
                size="small"
                sx={{ ml: 1 }}
              />
            )}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Invitations to join organizations and teams
          </Typography>
        </Box>
        <Button 
          variant="outlined" 
          startIcon={<RefreshIcon />}
          onClick={refreshInvitations}
          disabled={isLoading}
          size="small"
        >
          Refresh
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {actionError && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          onClose={() => setActionError(null)}
        >
          {actionError}
        </Alert>
      )}

      {actionSuccess && (
        <Alert
          severity="success"
          sx={{ mb: 2 }}
          onClose={() => setActionSuccess(null)}
        >
          {actionSuccess}
        </Alert>
      )}

      {/* DataGrid */}
      <Box sx={{ height: 400, width: '100%' }}>
        <DataGrid
          rows={invitations || []}
          columns={columns}
          loading={isLoading}
          getRowId={(row: Invitation) => row.id}
          autoHeight
          disableRowSelectionOnClick
          pageSizeOptions={[5, 10, 25]}
          initialState={{
            pagination: { paginationModel: { pageSize: 5 } },
            sorting: {
              sortModel: [{ field: 'createdAt', sort: 'desc' }],
            },
            filter: statusFilter === 'all' ? undefined : {
              filterModel: {
                // Context maps statuses to single-L 'canceled'; normalize the
                // 'cancelled' prop variant so the equals filter matches the data.
                items: [{ field: 'status', operator: 'equals', value: statusFilter === 'cancelled' ? 'canceled' : statusFilter }],
              },
            },
          }}
          localeText={{
            noRowsLabel: 'No invitations received yet',
            noResultsOverlayLabel: 'No invitations found',
            MuiTablePagination: {
              labelRowsPerPage: 'Invitations per page',
            },
          }}
          sx={{
            '& .MuiDataGrid-root': {
              border: 'none',
            },
            '& .MuiDataGrid-cell': {
              borderBottom: 'none',
            },
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: 'background.default',
              borderBottom: 'none',
            },
            '& .MuiDataGrid-virtualScroller': {
              backgroundColor: 'background.paper',
            },
          }}
        />
      </Box>
    </Box>
  );
};

export default PersonalInvitations;